export interface ScreenshotOptions {
  width?: number;
  height?: number;
  mattertags?: boolean;
  sweeps?: boolean;
}

export interface ScreenshotResult {
  dataUrl: string;
  width: number;
  height: number;
  timestamp: number;
}

export async function captureShowcaseScreenshot(
  showcase: any,
  options: ScreenshotOptions = {}
): Promise<ScreenshotResult> {
  if (!showcase || !showcase.Renderer || !showcase.Renderer.takeScreenShot) {
    throw new Error('Showcase SDK not available or Renderer.takeScreenShot not found');
  }

  const {
    width = 1920,
    height = 1080,
    mattertags = false,
    sweeps = false,
  } = options;

  try {

    const dataUrl = await showcase.Renderer.takeScreenShot(
      { width, height },
      { mattertags, sweeps }
    );

    if (!dataUrl || typeof dataUrl !== 'string' || dataUrl.length < 100) {
      throw new Error('Invalid screenshot data received');
    }

    return {
      dataUrl,
      width,
      height,
      timestamp: Date.now(),
    };
  } catch (error) {
    console.error('❌ Failed to capture screenshot:', error);
    throw error;
  }
}

export function dataUrlToBase64(dataUrl: string): string {

  const prefixes = [
    'data:image/jpeg;base64,',
    'data:image/jpg;base64,',
    'data:image/png;base64,',
  ];

  for (const prefix of prefixes) {
    if (dataUrl.startsWith(prefix)) {
      return dataUrl.substring(prefix.length);
    }
  }

  const match = dataUrl.match(/^data:image\/[^;]+;base64,(.+)$/);
  if (match) {
    return match[1];
  }

  return dataUrl;
}

export function estimateBase64Size(base64String: string): number {
  const sizeInBytes = base64String.length * 0.75;
  return sizeInBytes / (1024 * 1024);
}

export function validateScreenshotSize(dataUrl: string, maxSizeMB: number = 5): boolean {
  const base64 = dataUrlToBase64(dataUrl);
  const sizeMB = estimateBase64Size(base64);

  if (sizeMB > maxSizeMB) {
    throw new Error(`Screenshot size (${sizeMB.toFixed(2)}MB) exceeds maximum of ${maxSizeMB}MB`);
  }

  return true;
}

export function getBase64SizeInBytes(base64String: string): number {
  return base64String.length * 0.75;
}

export function getContentTypeFromDataUrl(dataUrl: string): string {
  const match = dataUrl.match(/^data:([^;]+);base64,/);
  if (match) {
    return match[1];
  }
  return 'image/png';
}

export function dataUrlToBlob(dataUrl: string): Blob {
  const base64 = dataUrlToBase64(dataUrl);
  const contentType = getContentTypeFromDataUrl(dataUrl);

  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
}

export function collectMediaMetadata(dataUrl: string): { fileName: string; contentType: string; fileSize: number; blob: Blob; uploadPath: string } {
  const contentType = getContentTypeFromDataUrl(dataUrl);
  const blob = dataUrlToBlob(dataUrl);
  const fileSize = blob.size;
  const extension = contentType.split('/')[1];
  const fileName = `screenshot-${Date.now()}.${extension}`;
  const uploadPath = `tai-chat/${fileName}`;

  return {
    fileName,
    contentType,
    fileSize,
    blob,
    uploadPath,
  };
}

